import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Stack, Avatar, Chip, CircularProgress } from '@mui/material';
import { Whatshot, Favorite } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { postService, Post } from '../services/postService';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

/**
 * Most-reacted posts from the last 7 days, scoped to what the viewer can actually see —
 * never a truly global ranking, since Private/Buddies/Followers/Group/Club visibility still
 * applies server-side. Shown as a panel above the feed alongside LeaderboardPanel/
 * DiscoverClubsPanel, matching their compact style.
 */
const TrendingPostsPanel: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    postService.getTrendingPosts()
      .then(results => { if (!cancelled) setPosts(results); })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  if (posts.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Whatshot fontSize="small" color="warning" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Trending This Week</Typography>
      </Stack>
      <Stack spacing={0.75}>
        {posts.map((post, i) => (
          <Stack
            key={post.postId}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, p: 0.5 }}
            onClick={() => navigate(`/post/${post.postId}`)}
          >
            <Typography variant="caption" sx={{ width: 16, fontWeight: 700, color: 'text.secondary' }}>
              {i + 1}
            </Typography>
            <Avatar
              src={post.authorProfilePhotoUrl ?? undefined}
              sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: 10, fontWeight: 700 }}
            >
              {!post.authorProfilePhotoUrl && (initials(post.authorDisplayName) || '?')}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {post.text || (post.type === 'DiveLog' ? post.diveLabel : post.type === 'EquipmentAdded' ? post.equipmentLabel : post.type === 'CheckIn' && post.diveSiteName ? `Diving at ${post.diveSiteName}` : post.type === 'ConditionsReport' && post.diveSiteName ? `Conditions at ${post.diveSiteName}` : post.authorDisplayName) || post.authorDisplayName}
              </Typography>
            </Box>
            <Chip
              size="small"
              variant="outlined"
              icon={<Favorite sx={{ fontSize: 12 }} />}
              label={post.reactionCount}
            />
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

export default TrendingPostsPanel;
